import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Send,
  Bot,
  User,
  Loader,
  Sparkles,
  Calendar,
  Users,
  BarChart2,
  Clock,
  Zap,
  TrendingUp,
  AlertTriangle,
  CreditCard,
  CheckCircle2,
  XCircle,
  ArrowRight,
} from 'lucide-react';
import { useAuth } from '../../contexts/auth/hooks';
import { supabase } from '../../lib/supabase';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { RealTimeActions, ActionItem } from '../../components/ai-chat/RealTimeActions';

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  toolsUsed?: string[];
}

interface TokensStatus {
  used: number;
  limit: number;
  remaining: number;
  percentage: number;
}

const QUICK_SUGGESTIONS = [
  {
    icon: Calendar,
    text: 'מה התורים שלי היום?',
    gradient: 'from-blue-500 to-cyan-500',
  },
  {
    icon: Users,
    text: 'הצג 5 לקוחות אחרונים',
    gradient: 'from-purple-500 to-pink-500',
  },
  {
    icon: BarChart2,
    text: 'כמה הרווחתי השבוע?',
    gradient: 'from-green-500 to-emerald-500',
  },
  {
    icon: Clock,
    text: 'תורים ממתינים לאישור',
    gradient: 'from-orange-500 to-red-500',
  },
  {
    icon: TrendingUp,
    text: 'מי הלקוחות הנאמנים?',
    gradient: 'from-indigo-500 to-purple-500',
  },
  {
    icon: Zap,
    text: 'תורים מבוטלים השבוע',
    gradient: 'from-yellow-500 to-orange-500',
  },
];

const CAPABILITIES = [
  {
    title: '📅 ניהול תורים מתקדם',
    items: [
      'צפייה בתורים לפי תאריך ו-סטטוס',
      'יצירה, עדכון וביטול תורים',
      'חיפוש זמנים פנויים',
      'סימון תורים כהושלמו או לא הגיעו',
    ],
  },
  {
    title: '👥 ניהול לקוחות מלא',
    items: [
      'חיפוש לקוחות לפי שם/טלפון',
      'יצירה ועדכון פרטי לקוחות',
      'צפייה בהיסטוריית לקוח',
      'זיהוי לקוחות נאמנים ו-VIP',
    ],
  },
  {
    title: '📊 דוחות ותובנות',
    items: [
      'הכנסות יומיות/שבועיות/חודשיות',
      'שירותים פופולריים',
      'אחוזי ביטולים ואי-הגעות',
      'השוואת תקופות',
    ],
  },
  {
    title: '⚙️ ניהול עסק',
    items: [
      'מידע על העסק',
      'שעות פעילות',
      'ניהול שירותים וצוות',
      'ביצועי צוות',
    ],
  },
];

export default function AIAssistantV3() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [tokens, setTokens] = useState<TokensStatus | null>(null);
  const [showWelcome, setShowWelcome] = useState(true);
  const [realtimeActions, setRealtimeActions] = useState<ActionItem[]>([]);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const businessId = user?.user_metadata?.business_id;

  useEffect(() => {
    if (businessId) {
      loadTokensStatus();
      loadRecentMessages();
    }
  }, [businessId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (messages.length > 0) {
      setShowWelcome(false);
    }
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

 const loadTokensStatus = async () => {
  try {
    const { data, error } = await supabase.rpc('get_remaining_tokens', {
      p_business_id: businessId,
    });

    if (error) throw error;

    // יכול להגיע כ-array, או כ-{remaining:{}}, או כאובייקט רגיל
    const raw = Array.isArray(data) ? data[0] : data;
    const payload = raw?.remaining ?? raw;

    if (!payload) return;

    const used = payload.tokens_used ?? 0;
    const limit = payload.tokens_limit ?? 0;
    const remaining = payload.tokens_remaining ?? 0;

    // שים לב: אצלך percentage זה USED מתוך LIMIT
    const percentage = limit > 0 ? (used / limit) * 100 : 0;

    setTokens({ used, limit, remaining, percentage });
  } catch (error) {
    console.error('[loadTokensStatus] error', error);
  }
};


  const loadRecentMessages = async () => {
    try {
      const { data } = await supabase
        .from('chat_histories')
        .select('role, content, created_at')
        .eq('business_id', businessId)
        .order('created_at', { ascending: true })
        .limit(20);

      if (data && data.length > 0) {
        setMessages(
          data.map((msg: any) => ({
            id: Math.random().toString(),
            role: msg.role,
            content: msg.content,
            timestamp: new Date(msg.created_at),
          }))
        );
      }
    } catch (error) {

    }
  };

  const sendMessage = async (messageText?: string) => {
    const text = messageText || input.trim();
    if (!text || loading) return;

    if (!businessId) {
      toast.error('לא זוהה עסק');
      return;
    }

    if (tokens && tokens.remaining <= 0) {
      toast.error('נגמרו הטוקנים! 🔋 לחץ כאן לרכישת טוקנים נוספים', {
        duration: 5000,
        icon: '💳',
      });
      return;
    }

    const userMessage: Message = {
      id: Math.random().toString(),
      role: 'user',
      content: text,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInput('');
    setLoading(true);
    setRealtimeActions([]);

    try {
      const session = await supabase.auth.getSession();
      const token = session.data.session?.access_token;

      const response = await fetch(
        `https://nkuqcyelxgyihrxyvitb.supabase.co/functions/v1/ai-agent-v3`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            message: text,
            businessId,
            userId: user?.id,
          }),
        }
      );

      const data = await response.json();

      if (data.error) {
        toast.error(data.error);
        return;
      }

      // Create visual actions for real-time display
      const actions: ActionItem[] = [];

      if (data.thinking_status) {
        actions.push({
          id: `action-thinking-${Date.now()}`,
          name: 'processing',
          status: 'completed' as const,
          message: data.thinking_status,
          timestamp: new Date(),
        });
      }

      if (data.thinking_history && Array.isArray(data.thinking_history)) {
        data.thinking_history.forEach((actionMsg: string, idx: number) => {
          actions.push({
            id: `action-${Date.now()}-${idx}`,
            name: data.toolsUsed?.[idx] || 'unknown',
            status: 'completed' as const,
            message: actionMsg,
            timestamp: new Date(),
          });
        });
      }

      if (data.toolsUsed && Array.isArray(data.toolsUsed) && actions.length === 0) {
        data.toolsUsed.forEach((tool: string, idx: number) => {
          actions.push({
            id: `action-tool-${Date.now()}-${idx}`,
            name: tool,
            status: 'completed' as const,
            message: `${tool} הושלם`,
            timestamp: new Date(),
          });
        });
      }

      if (actions.length === 0 && data.reply) {
        actions.push({
          id: `action-process-${Date.now()}`,
          name: 'process_request',
          status: 'completed' as const,
          message: 'מעבד בקשה',
          timestamp: new Date(),
        });
      }

      setRealtimeActions(actions);

      const assistantMessage: Message = {
        id: Math.random().toString(),
        role: 'assistant',
        content: data.reply || 'לא התקבלה תשובה',
        timestamp: new Date(),
        toolsUsed: data.toolsUsed,
      };

      setMessages((prev) => [...prev, assistantMessage]);
      await loadTokensStatus();
    } catch (error: any) {

      toast.error('שגיאה בשליחת הודעה');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const getTokenColor = () => {
    if (!tokens) return 'from-gray-400 to-gray-500';
    const percent = (tokens.remaining / tokens.limit) * 100;
    if (percent > 50) return 'from-green-400 to-emerald-500';
    if (percent > 20) return 'from-yellow-400 to-orange-500';
    return 'from-red-400 to-red-600';
  };

  const getTokenBgColor = () => {
    if (!tokens) return 'from-gray-50 to-gray-100';
    const percent = (tokens.remaining / tokens.limit) * 100;
    if (percent > 50) return 'from-green-50 to-emerald-50';
    if (percent > 20) return 'from-yellow-50 to-orange-50';
    return 'from-red-50 to-red-100';
  };

  const isTokensLow = tokens && (tokens.remaining / tokens.limit) * 100 <= 20;
  const isTokensOut = tokens && tokens.remaining <= 0;

  return (
    <div className="flex flex-col h-screen bg-gradient-to-br from-gray-50 via-blue-50/30 to-purple-50/30" dir="rtl">
      {/* Header */}
      <motion.div
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        className="bg-white/80 backdrop-blur-xl border-b border-gray-200/50 px-6 py-4 shadow-sm"
      >
        <div className="flex items-center justify-between max-w-6xl mx-auto">
          <div className="flex items-center gap-4">
            <motion.div
              animate={{
                rotate: [0, 10, -10, 0],
              }}
              transition={{ repeat: Infinity, duration: 3, ease: 'easeInOut' }}
              className="p-3 bg-gradient-to-br from-blue-600 to-purple-600 rounded-2xl shadow-lg shadow-blue-500/30"
            >
              <Sparkles className="w-7 h-7 text-white" />
            </motion.div>
            <div>
              <h1 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                העוזר החכם שלך
              </h1>
              <p className="text-sm text-gray-600 flex items-center gap-2">
                <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                60+ כלים מתקדמים - תמיד פעיל
              </p>
            </div>
          </div>

          {/* Tokens Display */}
          {tokens && tokens.limit > 0 && (
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className={`bg-gradient-to-br ${getTokenBgColor()} px-5 py-3 rounded-2xl border-2 ${
                isTokensOut
                  ? 'border-red-200'
                  : isTokensLow
                  ? 'border-orange-200'
                  : 'border-green-200'
              } shadow-sm`}
            >
              <div className="flex items-center gap-3 mb-2">
                <div className="flex items-center gap-2">
                  <Zap
                    className={`w-5 h-5 ${
                      isTokensOut
                        ? 'text-red-600'
                        : isTokensLow
                        ? 'text-orange-600'
                        : 'text-green-600'
                    }`}
                  />
                  <span className="text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    מכסת טוקנים
                  </span>
                </div>
                {isTokensLow && (
                  <motion.div
                    animate={{ scale: [1, 1.2, 1] }}
                    transition={{ repeat: Infinity, duration: 1.5 }}
                  >
                    <AlertTriangle className="w-4 h-4 text-orange-600" />
                  </motion.div>
                )}
              </div>

              <div className="flex items-baseline gap-2 mb-3">
                <span className="text-2xl font-bold text-gray-900">
                  {tokens.remaining.toLocaleString('he-IL')}
                </span>
                <span className="text-sm text-gray-500">
                  / {tokens.limit.toLocaleString('he-IL')}
                </span>
                <span className="text-xs text-gray-500 mr-auto">
                  ({(100 - tokens.percentage).toFixed(0)}% נותרו)
                </span>
              </div>

              <div className="w-64 h-2 bg-white/50 rounded-full overflow-hidden shadow-inner mb-2">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${Math.max(2, 100 - tokens.percentage)}%` }}
                  transition={{ duration: 0.8, ease: 'easeOut' }}
                  className={`h-full bg-gradient-to-r ${getTokenColor()} rounded-full shadow-sm`}
                />
              </div>

              {isTokensOut ? (
                <button
                  onClick={() => navigate('/subscription')}
                  className="w-full mt-2 px-4 py-2 bg-gradient-to-r from-red-600 to-red-700 text-white text-sm font-semibold rounded-lg hover:from-red-700 hover:to-red-800 transition-all shadow-md hover:shadow-lg flex items-center justify-center gap-2"
                >
                  <CreditCard className="w-4 h-4" />
                  רכוש טוקנים נוספים
                  <ArrowRight className="w-4 h-4" />
                </button>
              ) : isTokensLow ? (
                <button
                  onClick={() => navigate('/subscription')}
                  className="w-full mt-2 px-4 py-2 bg-gradient-to-r from-orange-600 to-orange-700 text-white text-xs font-medium rounded-lg hover:from-orange-700 hover:to-orange-800 transition-all flex items-center justify-center gap-2"
                >
                  <CreditCard className="w-3 h-3" />
                  שדרג לפני שנגמר
                </button>
              ) : (
                <p className="text-xs text-gray-600 text-center">
                  נוצל: {tokens.used.toLocaleString('he-IL')} החודש
                </p>
              )}
            </motion.div>
          )}
        </div>
      </motion.div>

      {/* Messages Area */}
      <div className="flex-1 overflow-y-auto px-6 py-6 space-y-6 max-w-6xl mx-auto w-full">
        {/* Welcome Screen */}
        {showWelcome && messages.length === 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center py-12"
          >
            <motion.div
              animate={{
                scale: [1, 1.05, 1],
                rotate: [0, 5, -5, 0],
              }}
              transition={{ repeat: Infinity, duration: 4, ease: 'easeInOut' }}
              className="inline-flex p-6 bg-gradient-to-br from-blue-600 to-purple-600 rounded-3xl shadow-2xl shadow-blue-500/30 mb-6"
            >
              <Bot className="w-16 h-16 text-white" />
            </motion.div>

            <h2 className="text-3xl font-bold text-gray-900 mb-3">שלום! איך אוכל לעזור? 👋</h2>
            <p className="text-gray-600 mb-8 max-w-2xl mx-auto">
              אני העוזר החכם שלך עם 60+ כלים מתקדמים. שאל אותי על תורים, לקוחות, דוחות ועוד...
            </p>

            {/* Quick Suggestions Grid */}
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-8 max-w-4xl mx-auto">
              {QUICK_SUGGESTIONS.map((suggestion, index) => {
                const Icon = suggestion.icon;
                return (
                  <motion.button
                    key={index}
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: index * 0.1 }}
                    whileHover={{ scale: 1.05, y: -5 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => sendMessage(suggestion.text)}
                    className="group relative overflow-hidden bg-white p-4 rounded-2xl border-2 border-gray-100 hover:border-blue-300 hover:shadow-xl transition-all"
                  >
                    <div className={`absolute inset-0 bg-gradient-to-br ${suggestion.gradient} opacity-0 group-hover:opacity-10 transition-opacity`} />
                    <div className={`p-3 bg-gradient-to-br ${suggestion.gradient} rounded-xl text-white mb-3 inline-flex`}>
                      <Icon className="w-6 h-6" />
                    </div>
                    <p className="text-sm font-medium text-gray-900 group-hover:text-blue-600 transition-colors">
                      {suggestion.text}
                    </p>
                  </motion.button>
                );
              })}
            </div>

            {/* Capabilities */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-4xl mx-auto text-right">
              {CAPABILITIES.map((cap, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.6 + index * 0.1 }}
                  className="bg-white/60 backdrop-blur-sm p-5 rounded-2xl border border-gray-200/50 shadow-sm"
                >
                  <h3 className="font-bold text-gray-900 mb-3">{cap.title}</h3>
                  <ul className="space-y-2">
                    {cap.items.map((item, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-gray-600">
                        <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Real-Time Actions Display */}
        <RealTimeActions
          actions={realtimeActions}
          isVisible={loading || realtimeActions.length > 0}
        />

        {/* Chat Messages */}
        <AnimatePresence>
          {messages.map((message, index) => (
            <motion.div
              key={message.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ delay: index * 0.05 }}
              className={`flex gap-4 ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              {message.role === 'assistant' && (
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  className="flex-shrink-0"
                >
                  <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-purple-600 rounded-2xl flex items-center justify-center shadow-lg shadow-blue-500/30">
                    <Bot className="w-6 h-6 text-white" />
                  </div>
                </motion.div>
              )}

              <div
                className={`max-w-2xl rounded-3xl px-5 py-4 shadow-lg ${
                  message.role === 'user'
                    ? 'bg-gradient-to-br from-blue-600 to-purple-600 text-white shadow-blue-500/30'
                    : 'bg-white text-gray-900 shadow-gray-200/50'
                }`}
              >
                <p className="whitespace-pre-wrap leading-relaxed">{message.content}</p>

                {message.toolsUsed && message.toolsUsed.length > 0 && (
                  <div className={`mt-3 pt-3 border-t ${message.role === 'user' ? 'border-white/20' : 'border-gray-200'} text-xs flex items-center gap-2 ${message.role === 'user' ? 'text-white/80' : 'text-gray-500'}`}>
                    <Zap className="w-3 h-3" />
                    <span>כלים: {message.toolsUsed.join(', ')}</span>
                  </div>
                )}

                <div className={`text-xs mt-2 ${message.role === 'user' ? 'text-white/70' : 'text-gray-400'}`}>
                  {message.timestamp.toLocaleTimeString('he-IL', {
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </div>
              </div>

              {message.role === 'user' && (
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  className="flex-shrink-0"
                >
                  <div className="w-10 h-10 bg-gradient-to-br from-gray-200 to-gray-300 rounded-2xl flex items-center justify-center shadow-md">
                    <User className="w-6 h-6 text-gray-600" />
                  </div>
                </motion.div>
              )}
            </motion.div>
          ))}
        </AnimatePresence>

        {/* Loading */}
        {loading && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="flex gap-4 justify-start"
          >
            <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-purple-600 rounded-2xl flex items-center justify-center shadow-lg shadow-blue-500/30">
              <Bot className="w-6 h-6 text-white" />
            </div>
            <div className="bg-white rounded-3xl px-5 py-4 shadow-lg shadow-gray-200/50">
              <div className="flex items-center gap-3">
                <Loader className="w-5 h-5 text-blue-600 animate-spin" />
                <span className="text-gray-600">מעבד את הבקשה...</span>
              </div>
            </div>
          </motion.div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input Area */}
      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        className="bg-white/80 backdrop-blur-xl border-t border-gray-200/50 px-6 py-4 shadow-lg"
      >
        <div className="max-w-6xl mx-auto">
          {isTokensOut ? (
            <div className="bg-gradient-to-r from-red-50 to-orange-50 border-2 border-red-200 rounded-2xl p-4 text-center">
              <div className="flex items-center justify-center gap-2 mb-2">
                <XCircle className="w-5 h-5 text-red-600" />
                <p className="font-semibold text-red-900">נגמרו הטוקנים בחבילה החודשית שלך</p>
              </div>
              <p className="text-sm text-red-700 mb-3">
                רכוש טוקנים נוספים כדי להמשיך להשתמש בעוזר החכם
              </p>
              <button
                onClick={() => navigate('/subscription')}
                className="px-6 py-3 bg-gradient-to-r from-red-600 to-red-700 text-white font-semibold rounded-xl hover:from-red-700 hover:to-red-800 transition-all shadow-lg hover:shadow-xl flex items-center justify-center gap-2 mx-auto"
              >
                <CreditCard className="w-5 h-5" />
                רכוש טוקנים עכשיו
                <ArrowRight className="w-5 h-5" />
              </button>
            </div>
          ) : (
            <div className="flex gap-3">
              <textarea
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="כתוב הודעה... (Enter לשליחה)"
                className="flex-1 resize-none rounded-2xl border-2 border-gray-200 bg-gray-50/50 px-5 py-4 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all placeholder:text-gray-400"
                rows={2}
                disabled={loading}
              />
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => sendMessage()}
                disabled={loading || !input.trim()}
                className="px-8 py-4 bg-gradient-to-br from-blue-600 to-purple-600 text-white rounded-2xl hover:from-blue-700 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg hover:shadow-xl shadow-blue-500/30 flex items-center gap-3 font-semibold"
              >
                <Send className="w-5 h-5" />
                <span>שלח</span>
              </motion.button>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
}
